import { makeStyles, tokens } from '@fluentui/react-components';
import { brandColors, brandFonts } from '../../theme/brandColors';
import { MetricInfoDialog } from './MetricInfoDialog';

/* Shared "real progress" metric block for the navy executive hero, used on every report
   page (epic list/progress, feature list/progress) so the numbers, labels and explanations
   stay identical. Shows three reconciling figures — Implemented (primary, = Resolved + Closed),
   Closed Off, and Resolved — each with an (i) MetricInfoDialog. The explanations are templated
   by `unit` so they reference the relevant work-item type. */

const useStyles = makeStyles({
  composite: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXS,
    borderLeft: '1px solid rgba(255,255,255,0.14)', paddingLeft: tokens.spacingHorizontalXXXL,
    '@media (max-width: 860px)': { borderLeft: 'none', paddingLeft: 0 },
  },
  label: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.14em', textTransform: 'uppercase', color: brandColors.lightBlue,
  },
  value: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXS },
  num: { fontFamily: brandFonts.heading, fontSize: '3.5rem', fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  pct: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, color: brandColors.lightBlue },
  meta: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase200, color: 'rgba(255,255,255,0.6)' },

  row: {
    display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXXL, rowGap: tokens.spacingVerticalL,
    marginTop: tokens.spacingVerticalL,
    paddingTop: tokens.spacingVerticalL, borderTop: '1px solid rgba(255,255,255,0.14)',
  },
  secondary: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalXXS },
  subLabel: {
    display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalS,
    fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase100, fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.78)',
  },
  valueSm: { display: 'flex', alignItems: 'baseline', gap: tokens.spacingHorizontalXXS },
  numSm: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightBold, lineHeight: '1', color: brandColors.white },
  pctSm: { fontFamily: brandFonts.heading, fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, color: 'rgba(255,255,255,0.7)' },
  metaSm: { fontFamily: brandFonts.mono, fontSize: tokens.fontSizeBase100, color: 'rgba(255,255,255,0.55)' },
});

export interface HeroProgressMetricsProps {
  /** Weighted completion (Story Point calcs model) — the primary big number. */
  percentProgress: number;
  /** Closed only (signed off). */
  percentClosed: number;
  /** Resolved only (built, awaiting sign-off). */
  percentResolved: number;
  /** Sub-line under the primary number — states which basis produced it. */
  progressMeta: string;
  closedMeta: string;
  resolvedMeta: string;
  /** Work-item type the metric aggregates, woven into the explanations (e.g. "user stories"). */
  unit?: string;
  /** Optional: New-scope share (discovery-new). When provided, a "New" figure renders after Resolved. */
  percentNew?: number;
  /** Sub-line under the New figure (e.g. "8 / 40 added after sign-off"). */
  newMeta?: string;
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export function HeroProgressMetrics({
  percentProgress, percentClosed, percentResolved,
  progressMeta, closedMeta, resolvedMeta, unit = 'work items',
  percentNew, newMeta,
}: HeroProgressMetricsProps) {
  const styles = useStyles();

  const progressInfo =
    `Weighted completion. ${cap(unit)} earn partial credit for how far they have got — New 0%, ` +
    `Active 20%, Resolved 60% (built, awaiting sign-off), Closed 100% (signed off) — and count in ` +
    `proportion to their story points, so a 13-point story moves this figure further than a ` +
    `1-pointer. Where too few ${unit} carry an estimate to weight them fairly, every item counts ` +
    `equally instead; the line beneath the number says which basis was used.`;
  const closedInfo =
    `${cap(unit)} formally signed off and Closed — the strictest measure of done. These count ` +
    `100% toward Progress.`;
  const resolvedInfo =
    `${cap(unit)} that are built and development-complete, but not yet signed off (Closed). ` +
    `These count 60% toward Progress.`;
  const newInfo =
    `Work legitimately added AFTER the original spec was signed off — needed to build the solution ` +
    `properly, rather than a change of mind. Shown as a share of all work items, so you can see how ` +
    `much the job grew beyond the original scope.`;

  return (
    <div className={styles.composite}>
      {/* PRIMARY — weighted progress (Story Point calcs model). */}
      <span className={styles.label}>
        Progress
        <MetricInfoDialog title="Progress" content={progressInfo} />
      </span>
      <div className={styles.value}>
        <span className={styles.num}>{percentProgress}</span>
        <span className={styles.pct}>%</span>
      </div>
      <span className={styles.meta}>{progressMeta}</span>

      {/* SECONDARY — Closed off (signed off) and Resolved (built, awaiting sign-off). */}
      <div className={styles.row}>
        <div className={styles.secondary}>
          <span className={styles.subLabel}>
            Closed Off
            <MetricInfoDialog title="Closed Off" content={closedInfo} />
          </span>
          <div className={styles.valueSm}>
            <span className={styles.numSm}>{percentClosed}</span>
            <span className={styles.pctSm}>%</span>
          </div>
          <span className={styles.metaSm}>{closedMeta}</span>
        </div>
        <div className={styles.secondary}>
          <span className={styles.subLabel}>
            Resolved
            <MetricInfoDialog title="Resolved" content={resolvedInfo} />
          </span>
          <div className={styles.valueSm}>
            <span className={styles.numSm}>{percentResolved}</span>
            <span className={styles.pctSm}>%</span>
          </div>
          <span className={styles.metaSm}>{resolvedMeta}</span>
        </div>
        {percentNew !== undefined && (
          <div className={styles.secondary}>
            <span className={styles.subLabel}>
              New
              <MetricInfoDialog title="New Scope" content={newInfo} />
            </span>
            <div className={styles.valueSm}>
              <span className={styles.numSm}>{percentNew}</span>
              <span className={styles.pctSm}>%</span>
            </div>
            <span className={styles.metaSm}>{newMeta}</span>
          </div>
        )}
      </div>
    </div>
  );
}
